import { createStyles, Theme } from '@material-ui/core/styles';
import { CreateCSSProperties, makeStyles } from '@material-ui/styles';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Label, PageTitle, Space } from '@/components';
import { SbpInvoiceAPIModelStatusEnum } from '@/generated/api/fps/core';
import { BlankLayout, StatusLayout } from '@/layout';
import { logout } from '@/model/global/actions';
import { makeSelectIsFrameView, makeSelectIsMobileView } from '@/model/global/selectors';
import { makeSelectDetails } from '@/model/invoice/selectors';

const useStyles = makeStyles((theme: Theme) => {
  const bodyBox: CreateCSSProperties = {
    textAlign: 'center',
    alignItems: 'center',
  };
  return createStyles({
    bodyBox: {
      ...bodyBox,
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
    },
    bodyBoxMobile: bodyBox,
  });
});

const selectDetails = makeSelectDetails();
const selectIsMobile = makeSelectIsMobileView();
const selectIsFrame = makeSelectIsFrameView();

export const InactiveProcessingStatusPage: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const isMobile = useSelector(selectIsMobile);
  const isFrame = useSelector(selectIsFrame);
  const details = useSelector(selectDetails);

  if (details?.value?.status === SbpInvoiceAPIModelStatusEnum.Succeeded) {
    return (
      <StatusLayout
        status="success"
        height={260}
        minHeight={220}
        header={{ title: <FormattedMessage id="invoice.pages.status.title" /> }}
        buttons={{
          main: {
            submit: false,
            type: 'secondary',
            onAct: async (): Promise<void> => {
              await logout()(dispatch);
            },
            title: (
              <FormattedMessage
                id={isFrame ? 'invoice.pages.status.submit.title.iframe' : 'invoice.pages.status.submit.title.main'}
              />
            ),
          },
        }}
      >
        <Space direction="vertical" size={2} className={isMobile ? classes.bodyBoxMobile : classes.bodyBox}>
          <PageTitle>
            <FormattedMessage id="invoice.pages.status.succeeded.title" />
          </PageTitle>
          <Label>
            <FormattedMessage id="invoice.pages.status.succeeded.content" />
          </Label>
        </Space>
      </StatusLayout>
    );
  }

  if (details?.value?.status === SbpInvoiceAPIModelStatusEnum.Cancelled) {
    return (
      <StatusLayout
        status="failed"
        height={260}
        minHeight={220}
        header={{ title: <FormattedMessage id="invoice.pages.status.title" /> }}
        buttons={{
          main: {
            submit: false,
            type: 'secondary',
            onAct: async (): Promise<void> => {
              await logout()(dispatch);
            },
            title: (
              <FormattedMessage
                id={isFrame ? 'invoice.pages.status.submit.title.iframe' : 'invoice.pages.status.submit.title.main'}
              />
            ),
          },
        }}
      >
        <Space direction="vertical" size={2} className={isMobile ? classes.bodyBoxMobile : classes.bodyBox}>
          <PageTitle>
            <FormattedMessage id="invoice.pages.status.cancelled.title" />
          </PageTitle>
          <Label>
            <FormattedMessage id="invoice.pages.status.cancelled.content" />
          </Label>
        </Space>
      </StatusLayout>
    );
  }

  if (details?.value?.status === SbpInvoiceAPIModelStatusEnum.Unknown) {
    return (
      <StatusLayout
        status="failed"
        height={280}
        minHeight={250}
        header={{ title: <FormattedMessage id="invoice.pages.status.title" /> }}
        buttons={{
          main: {
            submit: false,
            type: 'secondary',
            onAct: async (): Promise<void> => {
              await logout()(dispatch);
            },
            title: (
              <FormattedMessage
                id={isFrame ? 'invoice.pages.status.submit.title.iframe' : 'invoice.pages.status.submit.title.main'}
              />
            ),
          },
        }}
      >
        <Space direction="vertical" size={2} className={isMobile ? classes.bodyBoxMobile : classes.bodyBox}>
          <PageTitle>
            <FormattedMessage id="invoice.pages.status.unknown.title" />
          </PageTitle>
          <Label>
            <FormattedMessage id="invoice.pages.status.unknown.content" />
          </Label>
        </Space>
      </StatusLayout>
    );
  }

  return <BlankLayout />;
};

export default InactiveProcessingStatusPage;
